'use client'

import { useState } from 'react'
import {
  Button,
  CloseButton,
  Dialog,
  Portal,
  Spinner,
  Textarea,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AppField, CustomSelect, toaster } from '@/shared'
import { EBloodCollectionRejectReason } from '@/entities/blood-collection/model'
import { useRejectBloodCollectionMutation } from '@/entities/blood-collection/api'
import type { TBloodCollectionRejectReason } from '@/entities/blood-collection/model'

interface Props {
  open: boolean
  collectionStageId: string
  onClose: () => void
  onSuccess?: () => void
}

export function BloodCollectionRejectModal({
  open,
  collectionStageId,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<string[]>([
    EBloodCollectionRejectReason.POOR_WELLBEING,
  ])
  const [comment, setComment] = useState('')

  const { mutate: reject, isPending } =
    useRejectBloodCollectionMutation(collectionStageId)

  const reasonOptions = [
    {
      label: t(
        'laboratory_research.blood_collection.reject_reason.poor_wellbeing',
      ),
      value: EBloodCollectionRejectReason.POOR_WELLBEING,
    },
    {
      label: t('laboratory_research.blood_collection.reject_reason.other'),
      value: EBloodCollectionRejectReason.OTHER,
    },
  ]

  const handleClose = () => {
    setReason([EBloodCollectionRejectReason.POOR_WELLBEING])
    setComment('')
    onClose()
  }

  const handleSave = () => {
    if (!reason[0]) return

    reject(
      {
        reason: reason[0] as TBloodCollectionRejectReason,
        comment,
      },
      {
        onSuccess: () => {
          toaster.success({
            title: t('laboratory_research.blood_collection.reject_success'),
          })
          handleClose()
          onSuccess?.()
        },
        onError: () => {
          toaster.error({
            title: t('laboratory_research.blood_collection.reject_error'),
          })
        },
      },
    )
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose()
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="520px">
            <Dialog.Header pb={4}>
              <Dialog.Title fontSize="lg" fontWeight="bold">
                {t('laboratory_research.blood_collection.reject_modal_title')}
              </Dialog.Title>
              <Dialog.CloseTrigger
                asChild
                position="absolute"
                top={4}
                right={4}
              >
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={2}>
              <AppField.Root mb={4}>
                <AppField.Label fontSize="sm">
                  {t(
                    'laboratory_research.blood_collection.reject_reason_label',
                  )}
                </AppField.Label>
                <CustomSelect
                  options={reasonOptions}
                  value={reason}
                  onChange={setReason}
                  width="100%"
                />
              </AppField.Root>

              <AppField.Root>
                <AppField.Label fontSize="sm">
                  {t(
                    'laboratory_research.blood_collection.reject_comment_label',
                  )}
                </AppField.Label>
                <Textarea
                  placeholder={t(
                    'laboratory_research.blood_collection.reject_comment_placeholder',
                  )}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  borderRadius="md"
                  rows={4}
                  resize="vertical"
                />
              </AppField.Root>
            </Dialog.Body>

            <Dialog.Footer pt={4} justifyContent="flex-end">
              <Button
                bg="primary.solid"
                color="white"
                borderRadius="md"
                size="sm"
                px={6}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending || !reason[0]}
                onClick={handleSave}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('laboratory_research.blood_collection.save')
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
